import { BaseFeature } from '../../core/base-feature.js';

const metadata = {
    id: 'forced-browser',
    name: 'Forced Browser',
    version: '1.0',
    capabilities: ['forced-browser'],
    dependencies: []
};

export class ForcedBrowserFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);
        this.eventSubscribers = new Map();
        this.pendingRequests = new Map();
        this.state = {
            isOpen: false,
            url: '',
            title: '',
            canGoBack: false,
            canGoForward: false,
            allowlist: []
        };
    }

    static getMetadata() {
        return metadata;
    }

    async initialize() {}

    async onActivate() {}
    async onDeactivate() {}

    async handleMessage(message) {
        const { action, payload, messageId } = message;
        const originalMessageId = message.originalMessageId || payload?.originalMessageId;

        if (originalMessageId && this.pendingRequests.has(originalMessageId)) {
            this._handleResponse(originalMessageId, payload);
            return;
        }

        switch (action) {
            case 'page_updated':
                this.state.url = payload.url || '';
                this.state.title = payload.title || '';
                this.state.canGoBack = payload.canGoBack || false;
                this.state.canGoForward = payload.canGoForward || false;
                this._emitEvent('page_updated', this.state);
                break;

            case 'navigation_blocked':
                this._emitEvent('navigation_blocked', payload);
                break;

            case 'state_changed':
                Object.assign(this.state, payload);
                this._emitEvent('state_changed', this.state);
                break;

            case 'allowlist_changed':
                this.state.allowlist = payload.domains || [];
                this._emitEvent('allowlist_changed', this.state);
                break;

            case 'extensions_changed':
                this.state.extensions = payload.extensions || [];
                this._emitEvent('extensions_changed', this.state);
                break;
        }
    }

    // --- Public API for React components ---

    async open(url, allowlist) {
        const result = await this._sendRequest('open', { url, allowlist });
        if (result?.success) {
            this.state.isOpen = true;
            this.state.url = url || '';
            this.state.allowlist = allowlist || [];
            this._emitEvent('state_changed', this.state);
        }
        return result;
    }

    async close() {
        const result = await this._sendRequest('close', {});
        if (result?.success) {
            this.state.isOpen = false;
            this.state.url = '';
            this.state.title = '';
            this.state.canGoBack = false;
            this.state.canGoForward = false;
            this._emitEvent('state_changed', this.state);
        }
        return result;
    }

    async navigate(url) {
        return this._sendRequest('navigate', { url });
    }

    async goBack() {
        return this._sendRequest('go_back', {});
    }

    async goForward() {
        return this._sendRequest('go_forward', {});
    }

    async reload() {
        return this._sendRequest('reload', {});
    }

    async setAllowlist(domains) {
        const result = await this._sendRequest('set_allowlist', { domains });
        if (result?.success) {
            this.state.allowlist = domains;
        }
        return result;
    }

    async injectScript(script) {
        return this._sendRequest('inject_script', { script });
    }

    async getPageInfo() {
        return this._sendRequest('get_page_info', {});
    }

    async getDomContent(selector) {
        return this._sendRequest('get_dom_content', { selector });
    }

    async loadExtension(extensionPath) {
        return this._sendRequest('load_extension', { extensionPath });
    }

    async unloadExtension(extensionPath) {
        return this._sendRequest('unload_extension', { extensionPath });
    }

    async getExtensions() {
        return this._sendRequest('get_extensions', {});
    }

    async discoverExtensions() {
        return this._sendRequest('discover_extensions', {});
    }

    async tagElements() {
        return this._sendRequest('tag_elements', {});
    }

    async applyMutations(mutations) {
        return this._sendRequest('apply_mutations', { mutations });
    }

    // --- Subscription API ---

    subscribe(eventType, callback) {
        if (!this.eventSubscribers.has(eventType)) {
            this.eventSubscribers.set(eventType, new Set());
        }
        this.eventSubscribers.get(eventType).add(callback);
        return () => this.eventSubscribers.get(eventType)?.delete(callback);
    }

    // --- Private ---

    _emitEvent(eventType, data) {
        const subs = this.eventSubscribers.get(eventType);
        if (subs) {
            subs.forEach(cb => { try { cb(data); } catch (e) { console.error('[ForcedBrowser] subscriber error:', e); } });
        }
    }

    async _sendRequest(cmd, payload = {}) {
        return new Promise((resolve, reject) => {
            const messageId = this.core.sendFeatureMessage({
                feature: metadata.id,
                version: metadata.version,
                cmd,
                payload
            });

            this.pendingRequests.set(messageId, { resolve, reject });

            setTimeout(() => {
                if (this.pendingRequests.has(messageId)) {
                    this.pendingRequests.delete(messageId);
                    reject(new Error('Request timeout'));
                }
            }, 15000);
        });
    }

    _handleResponse(messageId, response) {
        const pending = this.pendingRequests.get(messageId);
        if (pending) {
            this.pendingRequests.delete(messageId);
            pending.resolve(response);
        }
    }

    enable() {}
    disable() {}

    async cleanup() {
        this.eventSubscribers.clear();
        this.pendingRequests.clear();
    }
}
